<script>
/**
 * One industry pack.
 *
 * Three things, in the order they matter to the bot:
 *
 *   Glossary  what a phrase means in THIS trade. One line each, plain words.
 *             "makan said about the game = lost money to the house."
 *   Examples  a message and the tone a human reads in it. `context` is the
 *             line before, for readings that only make sense with one —
 *             "great job" after a complaint is sarcasm, after a payout it is
 *             praise. These teach the bot more than the glossary does.
 *   Wording   what the bot says in this trade. Written ONCE, in whatever
 *             language it came to you; the other three are translated when
 *             you save. Leave a line empty and the platform's neutral
 *             version is used instead.
 *
 * Everything here is shared by every brand whose Industry names this pack.
 * The page says so, because the alternative is someone changing fifty brands
 * while believing they are changing one.
 */
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import i18n from '@/i18n'
import { sharedHelper } from '@/utils'
import { tonePackService } from '@/helpers/services/tonePack.service'

const LANGS = ['en', 'ms', 'zh', 'rojak']

export default {
  components: { Layout, PageHeader },
  data() {
    return {
      name: this.$route.params.name,
      loading: true,
      saving: false,
      /** One phrase per line — a textarea reads better than a list of inputs. */
      glossaryText: '',
      examples: [],
      copy: {},
      copyKeys: [],
      langs: LANGS,
      description: '',
      /** The box that is actually typed in. The rest follow from it on save. */
      writeLang: 'en',
      /** Keys whose other three languages are on screen. Off by default: the
       *  point of writing once is not having to look at four boxes. */
      expanded: {},
    }
  },
  computed: {
    items() {
      return [
        { text: i18n.t('module.tonePack'), to: { name: 'Industry-packs' } },
        { text: this.name, active: true },
      ]
    },
    toneOptions() {
      return ['neutral', 'praise', 'hostile', 'lost', 'unsure']
    },
    otherLangs() {
      return LANGS.filter((l) => l !== this.writeLang)
    },
  },
  mounted() {
    // Start on the console's own language: whoever is typing thinks in it.
    const current = (this.$i18n && this.$i18n.locale) || 'en'
    this.writeLang = LANGS.includes(current) ? current : 'en'
    this.load()
  },
  methods: {
    toneLabel(t) {
      const key = 'inbox.tone_' + t
      return this.$te(key) ? this.$t(key) : t
    },
    copyLabel(k) {
      const key = 'tonePack.key_' + k.replace('.', '_')
      return this.$te(key) ? this.$t(key) : k
    },
    async load() {
      this.loading = true
      try {
        const res = await tonePackService.get(this.name)
        const d = res.data || {}
        this.glossaryText = (d.glossary || []).join('\n')
        this.examples = (d.examples || []).map((e) => ({ text: e.text, context: e.context || '', tone: e.tone }))
        this.copyKeys = res.copy_keys || []
        // Every key gets a row even when empty, so the set of lines the bot
        // can say is visible rather than something you have to know about.
        const copy = {}
        for (const k of this.copyKeys) {
          copy[k] = {}
          for (const l of LANGS) copy[k][l] = (d.copy && d.copy[k] && d.copy[k][l]) || ''
        }
        this.copy = copy
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.loading = false
      }
    },
    toggle(k) {
      this.$set(this.expanded, k, !this.expanded[k])
    },
    /** How many of the other three already have words, for the line's summary. */
    filledCount(k) {
      return this.otherLangs.filter((l) => this.copy[k] && this.copy[k][l] && this.copy[k][l].trim()).length
    },
    addExample() {
      this.examples.push({ text: '', context: '', tone: 'neutral' })
    },
    removeExample(i) {
      this.examples.splice(i, 1)
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          description: this.description || undefined,
          glossary: this.glossaryText.split('\n').map((s) => s.trim()).filter(Boolean),
          examples: this.examples
            .filter((e) => e.text && e.text.trim())
            .map((e) => ({ text: e.text.trim(), context: e.context ? e.context.trim() : null, tone: e.tone })),
          // Empty strings are dropped rather than saved: an empty line means
          // "use the platform's", not "say nothing".
          copy: Object.fromEntries(
            Object.entries(this.copy)
              .map(([k, v]) => [k, Object.fromEntries(Object.entries(v).filter(([, s]) => s && s.trim()))])
              .filter(([, v]) => Object.keys(v).length),
          ),
        }
        const res = await tonePackService.save(this.name, payload)
        const n = ((res && res.translated) || []).length
        sharedHelper.alertSuccessMessage(n ? i18n.t('tonePack.savedTranslated', [n]) : i18n.t('tonePack.saved'))
        await this.load()
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.saving = false
      }
    },
    readError(e) {
      const d = e && e.response && e.response.data
      return (d && (d.error || d.message)) || i18n.t('message.error')
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="name" :items="items" />

    <div v-if="loading" class="card"><div class="card-body small text-muted">{{ $t('label.loading') }}</div></div>

    <template v-else>
      <div class="alert alert-secondary small">
        <i class="mdi mdi-information-outline mr-1"></i>{{ $t('tonePack.sharedWarning') }}
      </div>

      <!-- ── Glossary ──────────────────────────────────────────────────── -->
      <div class="card">
        <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('tonePack.glossary') }}</h6></div>
        <div class="card-body">
          <p class="small text-muted">{{ $t('tonePack.glossaryHint') }}</p>
          <b-form-textarea v-model="glossaryText" rows="7" :placeholder="$t('tonePack.glossaryPlaceholder')" />
        </div>
      </div>

      <!-- ── Examples ──────────────────────────────────────────────────── -->
      <div class="card mt-3">
        <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('tonePack.examples') }}</h6></div>
        <div class="card-body">
          <p class="small text-muted">{{ $t('tonePack.examplesHint') }}</p>
          <div class="table-responsive">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th style="width:40%">{{ $t('tonePack.exText') }}</th>
                  <th style="width:35%">{{ $t('tonePack.exContext') }}</th>
                  <th style="width:18%">{{ $t('tonePack.exTone') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(e, i) in examples" :key="i">
                  <td><b-form-input v-model="e.text" size="sm" /></td>
                  <td><b-form-input v-model="e.context" size="sm" :placeholder="$t('tonePack.exContextPlaceholder')" /></td>
                  <td>
                    <b-form-select v-model="e.tone" size="sm">
                      <b-form-select-option v-for="t in toneOptions" :key="t" :value="t">{{ toneLabel(t) }}</b-form-select-option>
                    </b-form-select>
                  </td>
                  <td class="text-right">
                    <b-button size="sm" variant="link" class="text-danger p-0" @click="removeExample(i)">
                      <i class="mdi mdi-close"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-button size="sm" variant="outline-secondary" @click="addExample">
            <i class="mdi mdi-plus mr-1"></i>{{ $t('tonePack.addExample') }}
          </b-button>
        </div>
      </div>

      <!-- ── Wording ───────────────────────────────────────────────────── -->
      <div class="card mt-3">
        <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('tonePack.wording') }}</h6></div>
        <div class="card-body">
          <p class="small text-muted">{{ $t('tonePack.wordingHint') }}</p>
          <b-form-group :label="$t('tonePack.writeLang')" class="mb-3 write-lang">
            <b-form-select v-model="writeLang" size="sm">
              <b-form-select-option v-for="l in langs" :key="l" :value="l">{{ l }}</b-form-select-option>
            </b-form-select>
          </b-form-group>
          <div v-for="k in copyKeys" :key="k" class="copy-block">
            <div class="copy-key">{{ copyLabel(k) }} <code class="small text-muted">{{ k }}</code></div>
            <b-form-textarea v-model="copy[k][writeLang]" rows="2" size="sm" />
            <div class="copy-foot">
              <button type="button" class="linkish" @click="toggle(k)">
                {{ expanded[k] ? $t('tonePack.hideLangs') : $t('tonePack.showLangs', [filledCount(k), otherLangs.length]) }}
              </button>
            </div>
            <div v-if="expanded[k]" class="lang-grid mt-2">
              <div v-for="l in otherLangs" :key="l">
                <label class="small text-muted mb-1">{{ l }}</label>
                <b-form-textarea v-model="copy[k][l]" rows="2" size="sm" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text-right my-3">
        <b-button variant="primary" :disabled="saving" @click="save">
          {{ saving ? $t('label.loading') : $t('label.submit') }}
        </b-button>
      </div>
    </template>
  </Layout>
</template>

<style scoped>
.copy-block { padding: 10px 0; border-bottom: 1px solid #eef0f4; }
.copy-block:last-of-type { border-bottom: 0; }
.copy-key { font-weight: 600; font-size: 13px; margin-bottom: 6px; }
.copy-foot { margin-top: 4px; }
.linkish { border: 0; background: none; padding: 0; font-size: 12px; color: #6f62d8; cursor: pointer; }
.linkish:hover { text-decoration: underline; }
.write-lang { max-width: 180px; }
.lang-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; }
</style>
